import React, { useState } from "react";
import { useQueries } from "../../context/QueryContext";
import { LEAD_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import toast from "react-hot-toast";

const CATEGORIES = [
    { value: "technical", label: "Technical" },
    { value: "process", label: "Process" },
    { value: "general", label: "General" },
    { value: "career", label: "Career" },
    { value: "other", label: "Other" },
];


export default function LeadQueries() {
    const { teamQueries, loading, replyToQuery, updateQueryStatus } = useQueries();
    const [expandedQuery, setExpandedQuery] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [submittingQueryId, setSubmittingQueryId] = useState(null);
    const [postedQueryId, setPostedQueryId] = useState(null);

    // ── Handle reply submission ─────────────────────────────────
    const handleReplySubmit = async (queryId) => {
        if (!replyText.trim()) {
            toast.error("Please enter a reply");
            return;
        }

        if (submittingQueryId === queryId || postedQueryId === queryId) {
            return;
        }

        setSubmittingQueryId(queryId);
        try {
            await replyToQuery(queryId, replyText);
            toast.success("Reply posted successfully!");
            setReplyText("");
            setPostedQueryId(queryId);
            setSubmittingQueryId(null);
        } catch (err) {
            setSubmittingQueryId(null);
            toast.error("Failed to post reply: " + err.message);
        }
    };

    // ── Handle status update ────────────────────────────────────
    const handleStatusChange = async (queryId, newStatus) => {
        try {
            await updateQueryStatus(queryId, newStatus);
            toast.success("Query status updated");
        } catch (err) {
            toast.error("Failed to update status: " + err.message);
        }
    };

    // ── Filter queries ─────────────────────────────────────────
    let filteredQueries = teamQueries;
    if (filterStatus !== "all") {
        filteredQueries = filteredQueries.filter((q) => q.status === filterStatus);
    }

    // Count stats
    const openCount = teamQueries.filter((q) => q.status === "Pending").length;
    const answeredCount = teamQueries.filter((q) => q.status === "In Progress").length;
    const closedCount = teamQueries.filter((q) => q.status === "Resolved").length;

    return (
        <Layout navItems={LEAD_NAV} panelLabel="Team Queries">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Team Queries & Doubts</h1>
                    <p className="page-subtitle">
                        Manage and respond to queries from your team members
                    </p>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="kpi-grid">
                <div className="kpi-card kpi-card--amber">
                    <div className="kpi-icon">
                        <span style={{ fontSize: "1.2rem" }}>❓</span>
                    </div>
                    <div className="kpi-body">
                        <span className="kpi-value">{openCount}</span>
                        <span className="kpi-label">Pending Queries</span>
                    </div>
                </div>

                <div className="kpi-card kpi-card--blue">
                    <div className="kpi-icon">
                        <span style={{ fontSize: "1.2rem" }}>⏳</span>
                    </div>
                    <div className="kpi-body">
                        <span className="kpi-value">{answeredCount}</span>
                        <span className="kpi-label">In Progress</span>
                    </div>
                </div>

                <div className="kpi-card kpi-card--green">
                    <div className="kpi-icon">
                        <span style={{ fontSize: "1.2rem" }}>✓</span>
                    </div>
                    <div className="kpi-body">
                        <span className="kpi-value">{closedCount}</span>
                        <span className="kpi-label">Resolved</span>
                    </div>
                </div>
            </div>

            {/* Filter */}
            <div className="form-card" style={{ marginBottom: "1.5rem" }}>
                <div className="form-group" style={{ margin: 0 }}>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        style={{ maxWidth: "200px" }}
                    >
                        <option value="all">All Queries</option>
                        <option value="Pending">Pending</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Resolved">Resolved</option>
                    </select>
                </div>
            </div>

            {/* Queries List */}
            <div className="dashboard-card">
                <div className="card-header">
                    <h3 className="card-title">Team Queries</h3>
                </div>
                {loading ? (
                    <div className="card-loading">Loading queries...</div>
                ) : filteredQueries.length === 0 ? (
                    <div className="card-empty">
                        {filterStatus === "all" ? "No queries from your team yet." : `No ${filterStatus} queries.`}
                    </div>
                ) : (
                    <div className="announcements-full-list">
                        {filteredQueries.map((query) => (
                            <div key={query.id} className={`announcement-card priority-${query.status === "Resolved" ? "low" : query.status === "Pending" ? "high" : "medium"}`}>
                                <div 
                                    className="announcement-card-header" 
                                    style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                                    onClick={() => {
                                        setExpandedQuery(expandedQuery === query.id ? null : query.id);
                                        setReplyText("");
                                        setPostedQueryId(null);
                                        setSubmittingQueryId(null);
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                        <span className="announcement-priority-dot" style={{ marginTop: 0 }} />
                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            <span style={{ fontSize: "0.75rem", color: "#2563eb", fontWeight: 600 }}>From: {query.askedByName}</span>
                                            <span style={{ fontWeight: 600 }}>{query.title}</span>
                                        </div>
                                        <span style={{ fontSize: "0.75rem", background: "#f3f4f6", padding: "0.15rem 0.5rem", borderRadius: "99px", color: "#4b5563" }}>
                                            {query.status?.toUpperCase()}
                                        </span>
                                    </div>
                                    <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>
                                        {expandedQuery === query.id ? "▲" : "▼"}
                                    </span>
                                </div>
                                
                                <p className="announcement-card-msg" style={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}>
                                    {query.question.slice(0, 100)}{query.question.length > 100 ? "..." : ""}
                                </p>

                                <div className="announcement-card-meta">
                                    <span>
                                        {CATEGORIES.find((c) => c.value === query.category)?.label || query.category}
                                    </span>
                                    <span>
                                        {query.createdAt?.toDate ? new Date(query.createdAt.toDate()).toLocaleDateString() : "Recently"}
                                    </span>
                                </div>

                                {/* Expanded View */}
                                {expandedQuery === query.id && (
                                    <div className="ann-read-panel" style={{ marginTop: "1rem", borderTop: "1px solid #e5e7eb", paddingTop: "1rem" }}>
                                        {/* Original Question */}
                                        <div style={{ marginBottom: "1.5rem", paddingBottom: "1rem", borderBottom: "1px solid #f3f4f6" }}>
                                            <h4 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: "0.5rem" }}>Team Member's Question:</h4>
                                            <div style={{ background: "#f8fafc", padding: "0.75rem", borderRadius: "0.375rem" }}>
                                                <p style={{ fontSize: "0.8rem", color: "#374151", whiteSpace: "pre-wrap", margin: 0 }}>{query.question}</p>
                                            </div>
                                        </div>

                                        {/* Existing Replies */}
                                        {query.replies && query.replies.length > 0 ? (
                                            <div style={{ marginBottom: "1.5rem" }}>
                                                <h4 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: "0.5rem" }}>Your Replies ({query.replies.length}):</h4>
                                                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                                                    {query.replies.map((reply) => (
                                                        <div key={reply.id} style={{ background: "#f0fdf4", padding: "0.75rem", borderRadius: "0.375rem", borderLeft: "3px solid #22c55e" }}>
                                                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                                                                <span style={{ fontWeight: 600, fontSize: "0.8rem", color: "#166534" }}>{reply.repliedByName}</span>
                                                                <span style={{ fontSize: "0.7rem", color: "#64748b" }}>
                                                                    {reply.createdAt?.toDate ? reply.createdAt.toDate().toLocaleString() : (reply.createdAt instanceof Date ? reply.createdAt.toLocaleString() : "Recently")}
                                                                </span>
                                                            </div>
                                                            <p style={{ fontSize: "0.8rem", color: "#14532d", margin: 0, whiteSpace: "pre-wrap" }}>{reply.reply}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : null}

                                        {/* Reply Form */}
                                        {query.status !== "Resolved" && (
                                            <div style={{ marginBottom: "1.5rem", paddingBottom: "1rem", borderBottom: "1px solid #f3f4f6" }}>
                                                <h4 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: "0.5rem" }}>
                                                    {query.replies?.length > 0 ? "Add Another Reply" : "Your Reply"}
                                                </h4>
                                                <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                                                    <textarea
                                                        value={replyText}
                                                        onChange={(e) => {
                                                            setReplyText(e.target.value);
                                                            if (postedQueryId === query.id) {
                                                                setPostedQueryId(null);
                                                            }
                                                        }}
                                                        placeholder="Type your response here..."
                                                        rows="3"
                                                        disabled={submittingQueryId === query.id}
                                                    />
                                                </div>
                                                <button 
                                                    onClick={() => handleReplySubmit(query.id)} 
                                                    className="btn-primary"
                                                    disabled={submittingQueryId === query.id || postedQueryId === query.id}
                                                >
                                                    {submittingQueryId === query.id ? "Posting..." : postedQueryId === query.id ? "Posted" : "Post Reply"}
                                                </button>
                                            </div>
                                        )}

                                        {/* Status Management */}
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: "0.5rem" }}>
                                            <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>Query Status:</span>
                                            <div style={{ display: "flex", gap: "0.5rem" }}>
                                                <button
                                                    onClick={() => handleStatusChange(query.id, "Pending")}
                                                    className="btn-secondary"
                                                    style={{ 
                                                        background: query.status === "Pending" ? "#ef4444" : "transparent",
                                                        color: query.status === "Pending" ? "white" : "#ef4444",
                                                        borderColor: "#ef4444",
                                                        padding: "0.2rem 0.6rem",
                                                        fontSize: "0.75rem"
                                                    }}
                                                >
                                                    Pending
                                                </button>
                                                <button
                                                    onClick={() => handleStatusChange(query.id, "In Progress")}
                                                    className="btn-secondary"
                                                    style={{ 
                                                        background: query.status === "In Progress" ? "#3b82f6" : "transparent",
                                                        color: query.status === "In Progress" ? "white" : "#3b82f6",
                                                        borderColor: "#3b82f6",
                                                        padding: "0.2rem 0.6rem",
                                                        fontSize: "0.75rem"
                                                    }}
                                                >
                                                    In Progress
                                                </button>
                                                <button
                                                    onClick={() => handleStatusChange(query.id, "Resolved")}
                                                    className="btn-secondary"
                                                    style={{ 
                                                        background: query.status === "Resolved" ? "#10b981" : "transparent",
                                                        color: query.status === "Resolved" ? "white" : "#10b981",
                                                        borderColor: "#10b981",
                                                        padding: "0.2rem 0.6rem",
                                                        fontSize: "0.75rem"
                                                    }}
                                                >
                                                    Resolved
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
}
